import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
} from "reactstrap";

import { unwrapResult } from '@reduxjs/toolkit';

import { getVeloxityAudienceGender } from "../../../slices/veloxity/audience/GenderReducer";
import { getVeloxityAudienceGeneration } from "../../../slices/veloxity/audience/GenerationReducer";
import { getVeloxityAudienceLocations } from "../../../slices/veloxity/audience/LocationReducer";
import { getVeloxityAudiencePurchasing } from "../../../slices/veloxity/audience/PurchasingPowerReducer";
import { getVeloxityAudienceInterests } from "../../../slices/veloxity/audience/InterestReducer";
import { getVeloxityAudienceSales } from "../../../slices/veloxity/audience/SalesPredictionReducer";
import { recalculateAudience } from '../../../slices/veloxity/audience/RecalculateReducer';
import { deleteAudience } from '../../../slices/veloxity/audience/DeleteRedujer';

import AudienceDetailPage from './AudienceDetailPage';
import AudienceMainPage from "./AudienceMainPage";

import { findAllVeloxityAudience } from '../../../slices/veloxity/audience/FindAllAudienceReducer';
import { getTopProfileTypes } from '../../../slices/veloxity/audience/TopProfileTypesReducer';
import { getVeloxityAudienceAnalysis } from '../../../slices/veloxity/audience/AnalysisReducer';

import showConfirmationAlert from '../../../components/custom/confirmationAlert';
import { showSuccessAlert, showErrorAlert } from '../../../components/custom/sweetalertHelper';

import Loader from '../../../components/common/Loader';

function Audience() {
  // Redux Dispatch
  const dispatch = useDispatch();
  const subscriptions = useSelector(state => state.getPermissionsList.subscriptions);
  
  const veloxityAudienceLoading = useSelector(state => state.findAllAudienceReducer.loading);
  const veloxityAudienceData = useSelector(state => state.findAllAudienceReducer.payload);
  const target = false;

  // Redux State
  const {
    genderRatios,
    audienceGeneration,
    audienceLocation,
    audiencePurchasing,
    probabilitys,
    audienceInterest,
    topProfileTypes,
    audienceAnalysis,
  } = useSelector(state => ({
    genderRatios: state.audienceGender.payload,
    audienceGeneration: state.audienceGeneration.payload,
    audienceLocation: state.audienceLocations.payload,
    audiencePurchasing: state.audiencePurchasing.payload,
    probabilitys: state.audienceSalesPrediction.payload,
    audienceInterest: state.audienceInterests.payload,
    topProfileTypes: state.topProfileTypes.payload,
    audienceAnalysis: state.audienceAnalysis.payload,
  }));

  const hasFeature = (feature) => {
    const audienceSubscription = subscriptions?.find(sub => sub.subscriptionType === "AUDIENCE");
    return audienceSubscription?.featureFlags?.[feature] === true;
  };

  const toggleDetailPage = () => {
    if (!veloxityAudienceLoading) {
      setShowDetailPage(!showDetailPage);
    }
  };
  
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [selectedAudienceData, setSelectedAudienceData] = useState(null);
  const [xx, setXx] = useState(false);
  const [showDetailPage, setShowDetailPage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredData, setFilteredData] = useState(veloxityAudienceData);

  const realLifeInterstOptions = veloxityAudienceData.map(item => ({
    value: item.name,
    label: item.name
  }));

  const handleAudience = (option) => {
    setXx(false);
    if (option) {
      setSelectedAudience(option);
      const audienceData = veloxityAudienceData.find(item => item.name === option.label);
      if (audienceData) {
        handleDetailClick(audienceData, false);
      } else {
        setSelectedAudienceData(null);
      }
    } else {
      setSelectedAudienceData(null);
    }
    setXx(true);
  };

  const updateLoading = async (isLoading) => {
    setIsLoading(isLoading);
    if (isLoading === false) {
      await dispatch(findAllVeloxityAudience(target));
    }
  };

  const handleDetailClick = async (audienceData, shouldToggleDetailPage = true) => {
    await setIsLoading(true);
    const selectedOption = realLifeInterstOptions.find(option => option.label === audienceData.name);
    const audienceId = audienceData.audienceId;

    if (selectedOption) {
      setSelectedAudience({
        ...selectedOption,
        id: audienceId
      });
      setSelectedAudienceData(audienceData);
    } else {
      setSelectedAudience(null);
    }

    const fetchPromises = [
      dispatch(getVeloxityAudienceGeneration({ clientId: audienceId })),
      dispatch(getVeloxityAudienceLocations({ clientId: audienceId })),
      dispatch(getVeloxityAudiencePurchasing({ clientId: audienceId })),
      dispatch(getVeloxityAudienceInterests({ clientId: audienceId })),
      dispatch(getTopProfileTypes({ clientId: audienceId })),
      dispatch(getVeloxityAudienceGender({ clientId: audienceId })),
    ];

    if (hasFeature("analytic::sales_prediction")) {
      fetchPromises.push(dispatch(getVeloxityAudienceSales({ clientId: audienceId })));
    }

    await Promise.all(fetchPromises);

    setXx(true);
    dispatch(getVeloxityAudienceAnalysis({ clientId: audienceId }));

    if (shouldToggleDetailPage) {
      toggleDetailPage();
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const onReCalculetedClick = async (audienceId) => {
    try {
      await dispatch(recalculateAudience(audienceId));
      await dispatch(findAllVeloxityAudience(target));
    } catch (error) {
      console.error('Error in recalculation:', error);
    }
  };

  const deleteAudienceClick = async (item) => {
    showConfirmationAlert("Are you sure you want to delete this audience?", async () => {
      updateLoading(true);
      try {
        const actionResult = await dispatch(deleteAudience({ audienceId: item.audienceId }));
        const { success, message } = unwrapResult(actionResult);
        updateLoading(false);
        if (success) {
          showSuccessAlert("Success!", message, "success");
        } else {
          showErrorAlert("Error!", message, "error");
        }
      } catch (error) {
        updateLoading(false);
        showErrorAlert("Error!", error.message || "Deletion failed due to an unexpected error.", "error");
      }
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      await setIsLoading(true);
      await dispatch(findAllVeloxityAudience(target));
      await setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (veloxityAudienceData && veloxityAudienceData.length > 0) {
      setFilteredData(veloxityAudienceData);
    }
  }, [veloxityAudienceData]);

  document.title = "AIMEE - Audience Analytics";

  return (
    <React.Fragment>
      <Container fluid></Container>
      {
        isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Loader />
          </div>
        ) : (showDetailPage ? (
          <AudienceDetailPage
            selectedAudience={selectedAudience}
            handleAudience={handleAudience}
            xx={xx}
            veloxityAudienceData={veloxityAudienceData}
            genderRatios={genderRatios}
            audienceGeneration={audienceGeneration}
            audienceLocation={audienceLocation}
            audiencePurchasing={audiencePurchasing}
            audienceInterest={audienceInterest}
            probabilitys={probabilitys}
            toggleDetailPage={toggleDetailPage}
            topProfileTypes={topProfileTypes}
            audienceAnalysis={audienceAnalysis}
            selectedAudienceData={selectedAudienceData}
            updateLoading={updateLoading}
            audienceId={selectedAudience?.id}
          />
        ) : (
          <AudienceMainPage
            filteredData={filteredData}
            handleDetailClick={handleDetailClick}
            onReCalculetedClick={onReCalculetedClick}
            updateLoading={updateLoading}
            deleteAudienceClick={deleteAudienceClick}
          />
        ))}
    </React.Fragment>
  );
}

export default Audience;
